import { useMemo } from 'react';
import { HistoryStatues } from '../../helpers/types';

interface UseExpandedDerivedDataParams {
  score: number;
  gsbLiveInfo: any;
  dnsRecords: any;
  statusHistory: HistoryStatues[] | undefined;
  takedownStatuses: any[] | undefined;
}

const useExpandedDerivedData = ({
  score,
  gsbLiveInfo,
  dnsRecords,
  statusHistory,
  takedownStatuses,
}: UseExpandedDerivedDataParams) => {
  const preCrimeLevel = useMemo(() => {
    if (score >= 0.8) return 'malicious' as const;
    if (score >= 0.7) return 'suspicious' as const;
    if (score >= 0.5) return 'unknown' as const;
    return 'benign' as const;
  }, [score]);

  const gsbThreatTypes = useMemo(() => {
    return gsbLiveInfo?.threat?.threatTypes || [];
  }, [gsbLiveInfo]);

  const dnsARecord = useMemo(() => {
    const records = dnsRecords?.items?.[0]?.data;
    const aRecord = records?.find((item: any) => item.record_type === 'A');
    return aRecord?.address ?? 'NA';
  }, [dnsRecords]);

  const filteredDnsRecords = useMemo(() => {
    const records = dnsRecords?.items?.[0]?.data;
    return records?.filter(
      (item: any) =>
        item.record_type === 'A' ||
        item.record_type === 'AAAA' ||
        item.record_type === 'MX' ||
        item.record_type === 'NS',
    );
  }, [dnsRecords]);

  const sortedStatusHistory = useMemo(() => {
    if (!statusHistory) return [];
    return [...statusHistory].sort(
      (a: HistoryStatues, b: HistoryStatues) =>
        new Date(b.Created).getTime() - new Date(a.Created).getTime(),
    );
  }, [statusHistory]);

  const findStatusNameById = useMemo(() => {
    const statusMap = new Map<number, string>();
    takedownStatuses?.forEach((s: any) => statusMap.set(s.id, s.name));
    return (id: number) => statusMap.get(id) || 'Unknown';
  }, [takedownStatuses]);

  return {
    preCrimeLevel,
    gsbThreatTypes,
    dnsARecord,
    filteredDnsRecords,
    sortedStatusHistory,
    findStatusNameById,
  };
};

export default useExpandedDerivedData;
